<template>
  <div>
    <a
      :href="viewUrl || '#'"
      :data-test-id="`${dataTestId}-view-btn-${rowIndex}`"
      @click.prevent="!disabled && $emit('clicked')"
      :class="[
        disabled
          ? 'tw-inline-flex tw-opacity-50 tw-cursor-not-allowed'
          : 'tw-inline-flex',
      ]"
    >
      <cl-table-action-button
        :data-test-id="`${dataTestId}-view-action-btn-${rowIndex}`"
        :readonly="disabled"
        :class="[disabled ? 'tw-inline-flex tw-opacity-50' : 'tw-inline-flex']"
      >
        <cb-icon-view :width="18" />
      </cl-table-action-button>
    </a>
    <authorisation-wrapper
      :resource="ACCOUNTS"
      :action="WRITE"
      v-slot="{ isAllowed: isWriteAllowed }"
    >
      <cl-table-action-button
        :data-test-id="`${dataTestId}-edit-btn-${rowIndex}`"
        :readonly="!isWriteAllowed || disabled"
        :class="[disabled ? 'tw-inline-flex tw-opacity-50' : 'tw-inline-flex']"
        @click="onEditClick"
      >
        <cl-spinner
          :loading-text="$t('loading')"
          v-if="loading"
          :size="'small'"
          class="mr-2"
        />
        <cb-icon-edit v-else :width="18" />
      </cl-table-action-button>
    </authorisation-wrapper>
    <component
      :is="editComponent"
      v-if="modalVisible"
      :visible="modalVisible"
      v-bind="editComponentProps"
      @on-update="customerUpdated"
      @on-close="modalVisible = false"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, toRefs } from "vue";
import { useOrganisationsStore } from "@/store/organisations.store";
import {
  ConsumerOrganisationLicencesResponse,
  ResellerOrganisationResponse,
} from "@/types/licenceSolutions.ts";
import { Organisation } from "@/types/organisatio.ts";

import { AuthorisationWrapper, ACCOUNTS, WRITE } from "@/authorisation";
import TUpdateCustomerIndex from "@/modals/t-update-customer/t-update-customer.index.vue";
import TUpdateMspsIndex from "@/modals/t-update-msps/t-update-msps.index.vue";
import { TIER } from "@/constants/tier";
import { clearLicencesCache } from "@/utils/licenceApi";
import { useLicenceStore } from "@/modules/Usage/store/licence.store";

interface Props {
  dataTestId: string;
  customerUuid: string;
  mspUuid: string;
  rowIndex: number;
  organisationType: string;
  viewUrl?: string;
  disabled?: boolean;
}
const props = defineProps<Props>();
const { dataTestId, customerUuid, mspUuid, organisationType, viewUrl } =
  toRefs(props);

const emit = defineEmits(["clicked", "updated"]);

const licenceStore = useLicenceStore();
const organisationsStore = useOrganisationsStore();

const loading = ref<boolean>(false);
const modalVisible = ref<boolean>(false);
const customerOrganisationObj = ref<Organisation>({} as Organisation);
const customerLicences = ref<ConsumerOrganisationLicencesResponse>(
  {} as ConsumerOrganisationLicencesResponse,
);
const mspLicences = ref<ResellerOrganisationResponse>(
  {} as ResellerOrganisationResponse,
);

const isCustomer = computed(() => organisationType.value === TIER.customer);
const isMsp = computed(() => organisationType.value === TIER.msps);
const editComponent = computed(() =>
  isMsp.value ? TUpdateMspsIndex : TUpdateCustomerIndex,
);
const editComponentProps = computed(() => {
  if (isMsp.value) {
    return {
      orginisationObject: customerOrganisationObj.value,
      listAllLicences: mspLicences.value,
    };
  }

  return {
    orginisationObject: customerOrganisationObj.value,
    listAllLicences: customerLicences.value,
    listLicencesMsp: mspLicences.value,
  };
});

const customerUpdated = () => {
  clearLicencesCache();
  modalVisible.value = false;
  emit("updated");
};

const onEditClick = async () => {
  loading.value = true;
  try {
    const response = (await organisationsStore.showOrg({
      uuid: customerUuid.value,
    }))!;
    customerOrganisationObj.value = response.data;
    if (isCustomer.value) {
      customerLicences.value = (await licenceStore.listAllLicences({
        uuid: customerUuid.value,
      })) as ConsumerOrganisationLicencesResponse;
      mspLicences.value = (await licenceStore.listAllLicences({
        uuid: mspUuid.value,
      })) as ResellerOrganisationResponse;
    } else {
      mspLicences.value = (await licenceStore.listAllLicences({
        uuid: customerUuid.value,
      })) as ResellerOrganisationResponse;
    }
  } catch (e) {
    /* eslint-disable no-console */
    console.error(e);
    loading.value = false;
  } finally {
    modalVisible.value = true;
    loading.value = false;
  }
};
</script>
